import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { Eyebrow, NFCCard } from "../components/ui.jsx";
import { useLocaleNav } from "../lib/useLocaleNav.js";
import { goToBooking } from "../lib/handoff.js";

export default function How() {
  const { t } = useTranslation();
  const go = useLocaleNav();
  return (
    <main>
      <ScrollStory />

      <section id="how-next" className="bg-bold" style={{ padding: "120px 0" }}>
        <div className="container">
          <h2 className="tt-h tt-h-xl" style={{ color: "var(--bg)" }}>
            {t("how.ctaHeadline")} <span style={{ opacity: .85 }}>{t("how.ctaHeadlineItalic")}</span>
          </h2>
          <div style={{ marginTop: 40 }}>
            <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); goToBooking(); }} style={{ background: "var(--bg)", color: "var(--ink)" }}>
              {t("common.bookDemo")} <Icon name="arrow-right" size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ----------------------------- hooks & utils ---------------------------- */

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

function useScrollProgress(ref, enabled) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) { setP(0); return; }
      const next = Math.max(0, Math.min(1, -rect.top / total));
      setP(next);
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    measure();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [ref, enabled]);
  return p;
}

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const beat = (p, start, end) => clamp01((p - start) / (end - start));

/* -------------------------------- story --------------------------------- */

function ScrollStory() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const p = useScrollProgress(ref, !reduced);

  if (reduced) return <StaticFallback />;

  // Global beats
  const bIntroOut  = beat(p, 0.015, 0.08);                 // headline fades as scroll begins
  const bSlideIn   = easeInOut(beat(p, 0.06, 0.20));        // card + phone slide to center
  const bTap       = beat(p, 0.18, 0.22);                  // tap moment, ripple
  const bCardOut   = beat(p, 0.22, 0.30);                  // card fades after tap
  const bFeedback  = beat(p, 0.22, 0.62);                  // feedback screen flow
  const bSwap      = easeInOut(beat(p, 0.62, 0.74));        // phone 1 out, phone 2 in
  const bManager   = beat(p, 0.74, 1.00);                  // manager dashboard flow

  // Card transform
  const cardX = (1 - bSlideIn) * -700 + bSlideIn * 0;
  const cardOpacity = clamp01(bSlideIn * 2) * (1 - bCardOut);
  const cardScale = 1 - bCardOut * 0.15;

  // Phone 1 transform — slides in, then slides left out as Phone 2 enters
  const phone1XIn = (1 - bSlideIn) * 700 + bSlideIn * 0;
  const phone1XOut = bSwap * -700;
  const phone1X = phone1XIn + phone1XOut;
  const phone1Opacity = clamp01(bSlideIn * 2) * (1 - bSwap);

  // Phone 2 transform — slides in from right as Phone 1 leaves
  const phone2X = (1 - bSwap) * 700 + bSwap * 0;
  const phone2Opacity = bSwap;

  // Flying push notification — materialises above phone 1's Send button when
  // the feedback is "sent", arcs across to phone 2's notification feed during
  // the swap, then fades into the dashboard.
  const bNotifIn   = clamp01((p - 0.56) / 0.04);              // fades in over the Send button
  const bNotifFly  = clamp01((p - 0.60) / 0.14);              // travels phone 1 → phone 2
  const bNotifLand = clamp01((p - 0.76) / 0.04);              // settles + fades on arrival
  const notifOpacity = bNotifIn * (1 - bNotifLand);
  const notifX = -110 + bNotifFly * 220;                       // left edge of phone 1 → right edge of phone 2
  // Arc: starts near phone 1's Send button (Y≈200 below center), rises into
  // the gap between phones, then settles near phone 2's notification feed (Y≈-40).
  const arcLift = Math.sin(bNotifFly * Math.PI) * 60;
  const notifY = 200 - bNotifFly * 240 - arcLift;
  const notifScale = 0.7 + bNotifIn * 0.3 - bNotifLand * 0.12;

  const sectionTopRef = ref;
  const jumpToProgress = (targetP) => {
    const el = sectionTopRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const sectionTop = window.scrollY + rect.top;
    window.scrollTo({ top: sectionTop + total * targetP + 1, behavior: "smooth" });
  };
  const jumpPastSection = () => {
    const next = document.getElementById("how-next");
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      ref={ref}
      aria-label={t("how.ariaSection")}
      style={{
        position: "relative",
        height: "640vh",
        background: "var(--bg)",
        borderBottom: "1px solid var(--line)"
      }}
    >
      <div style={{ position: "sticky", top: 80, height: "calc(100vh - 80px)", overflow: "hidden" }}>

        {/* Animated headline — fades in on mount, fades out on scroll */}
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 5,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: 24, textAlign: "center",
            pointerEvents: "none"
          }}
        >
          <div style={{ animation: "tt-hero-in 900ms cubic-bezier(.2,.7,.2,1) both" }}>
            <div
              style={{
                opacity: 1 - bIntroOut,
                transform: `translateY(${-bIntroOut * 16}px)`,
                transition: "opacity .15s linear"
              }}
            >
              <Eyebrow>{t("how.eyebrow")}</Eyebrow>
              <h2 className="tt-h tt-h-xl" style={{ margin: "16px auto 0", maxWidth: 900 }}>
                {t("how.headline")}<br /><span style={{ color: "var(--persimmon)" }}>{t("how.headlineItalic")}</span>
              </h2>
            </div>
          </div>
        </div>

        {/* Stage */}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "relative", width: 1, height: 1 }}>

            {/* Wave ripple — tap moment */}
            {bTap > 0.01 && bCardOut < 0.95 && <WaveRipple />}

            {/* NFC card — slides in from left, fades after tap */}
            <div
              style={{
                position: "absolute", left: 0, top: 0,
                transform: `translate(calc(-50% + ${cardX}px), -50%) rotate(-4deg) scale(${cardScale})`,
                opacity: cardOpacity,
                willChange: "transform, opacity",
                zIndex: 1
              }}
            >
              <NFCCard size="md" />
            </div>

            {/* Phone 1 — guest feedback */}
            <div
              style={{
                position: "absolute", left: 0, top: 0,
                transform: `translate(calc(-50% + ${phone1X}px), -50%)`,
                opacity: phone1Opacity,
                willChange: "transform, opacity",
                zIndex: 2
              }}
            >
              <IPhoneFrame>
                <FeedbackScreen p={bFeedback} onSubmit={() => jumpToProgress(0.74)} />
              </IPhoneFrame>
            </div>

            {/* Phone 2 — manager dashboard */}
            <div
              style={{
                position: "absolute", left: 0, top: 0,
                transform: `translate(calc(-50% + ${phone2X}px), -50%)`,
                opacity: phone2Opacity,
                willChange: "transform, opacity",
                zIndex: 3
              }}
            >
              <IPhoneFrame>
                <ManagerScreen p={bManager} onFix={jumpPastSection} />
              </IPhoneFrame>
            </div>

            {/* Flying push notification — carries the feedback from phone 1
                to phone 2 during the swap. Sits above both phones (zIndex 5)
                so it stays visible while the phones slide past each other. */}
            {notifOpacity > 0.01 && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute", left: 0, top: 0,
                  transform: `translate(calc(-50% + ${notifX}px), calc(-50% + ${notifY}px)) scale(${notifScale})`,
                  opacity: notifOpacity,
                  willChange: "transform, opacity",
                  zIndex: 5,
                  pointerEvents: "none"
                }}
              >
                <FlyingNotification />
              </div>
            )}
          </div>
        </div>

        {/* Scroll hint — visible from page load */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
            fontFamily: "Inter, sans-serif", fontSize: 12, letterSpacing: ".24em", textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--ink)",
            opacity: clamp01(1 - p * 6),
            transition: "opacity .3s",
            pointerEvents: "none",
            zIndex: 6
          }}
        >
          <span>{t("how.scrollHint")}</span>
          <span aria-hidden="true" style={{ width: 1.5, height: 36, background: "currentColor", borderRadius: 1, opacity: .55 }} />
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ animation: "tt-scrollhint 1.6s ease-in-out infinite" }}>
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Animation keyframes */}
        <style>{`
          @keyframes tt-hero-in {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes tt-scrollhint { 0%,100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
          @keyframes tt-wave {
            from { transform: translate(-50%, -50%) scale(0); opacity: .55; }
            to   { transform: translate(-50%, -50%) scale(10); opacity: 0; }
          }
          @keyframes tt-sendblink {
            0%, 100% { background: #0E0E0E; box-shadow: 0 0 0 0 rgba(255, 90, 26, 0); }
            50%      { background: #FF5A1A; box-shadow: 0 8px 24px -8px rgba(255, 90, 26, .7); }
          }
          @keyframes tt-fixblink {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 26, .0); transform: scale(1); }
            50%      { box-shadow: 0 0 0 6px rgba(255, 90, 26, .25); transform: scale(1.02); }
          }
          @keyframes tt-notifblink {
            0%, 100% { box-shadow: 0 0 0 1.5px rgba(255, 90, 26, .35); background: #fff; }
            50%      { box-shadow: 0 0 0 2.5px #FF5A1A; background: #FFF7F0; }
          }
          @keyframes tt-caret { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
          @media (prefers-reduced-motion: reduce) {
            @keyframes tt-hero-in    { from { opacity: 0; } to { opacity: 1; } }
            @keyframes tt-scrollhint { 0%, 100% { transform: translateY(0); } }
            @keyframes tt-wave       { from { opacity: 0; } to { opacity: 0; } }
            @keyframes tt-sendblink  { 0%, 100% { background: #0E0E0E; } }
            @keyframes tt-fixblink   { 0%, 100% { transform: scale(1); } }
            @keyframes tt-notifblink { 0%, 100% { box-shadow: 0 0 0 1.5px #FF5A1A; } }
          }
        `}</style>
      </div>
    </section>
  );
}

function FlyingNotification() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: "10px 14px 11px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minWidth: 240,
        maxWidth: 280,
        boxShadow:
          "0 24px 50px -16px rgba(16,14,12,.32), 0 10px 22px -10px rgba(255,90,26,.28), 0 0 0 1px rgba(0,0,0,.04)",
        border: "1px solid rgba(0,0,0,.05)",
        fontFamily: "-apple-system, 'Inter', sans-serif"
      }}
    >
      <div
        style={{
          width: 32, height: 32,
          borderRadius: 7,
          background: "linear-gradient(135deg, #FF5A1A 0%, #F2A33E 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 4px 10px -4px rgba(255,90,26,.55)"
        }}
      >
        <EmojiIcon type="love" size={22} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0E0E0E", letterSpacing: "-.005em" }}>TableTalk</span>
          <span style={{ fontSize: 10, color: "#9A9A9A" }}>now</span>
        </div>
        <div style={{ marginTop: 2, fontSize: 12, lineHeight: 1.35, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {t("how.notification.body")}
        </div>
      </div>
    </div>
  );
}

function WaveRipple() {
  const rings = [0, 0.18, 0.36];
  return (
    <div style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 4 }}>
      {rings.map((delay, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: 0, top: 0,
            width: 90, height: 90,
            border: "2px solid var(--persimmon)",
            borderRadius: 99,
            transform: "translate(-50%, -50%) scale(0)",
            animation: `tt-wave 1.4s cubic-bezier(.2,.7,.2,1) ${delay}s forwards`
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------ iPhone frame ----------------------------- */

function IPhoneFrame({ children }) {
  return (
    <div
      style={{
        width: 280,
        height: 590,
        background: "linear-gradient(180deg, #1c1c1e 0%, #2c2c2e 100%)",
        borderRadius: 46,
        padding: 7,
        boxShadow:
          "0 40px 80px -30px rgba(16,14,12,.45), 0 0 0 1.5px rgba(255,255,255,.08) inset, 0 0 0 2px rgba(0,0,0,.18)",
        position: "relative"
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "#fff",
          borderRadius: 40,
          position: "relative",
          overflow: "hidden"
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 9,
            left: "50%",
            transform: "translateX(-50%)",
            width: 92,
            height: 26,
            background: "#000",
            borderRadius: 99,
            zIndex: 100
          }}
        />
        {children}
      </div>
    </div>
  );
}

/* --------------------------- feedback screen (P1) ------------------------ */

function FeedbackScreen({ p, onSubmit }) {
  const { t } = useTranslation();
  // p: 0..1 within the feedback phase. Screen materialises, Love it gets
  // clicked, then the submit button activates and starts blinking orange.
  const screenOp     = clamp01(p / 0.05);                  // header + question + emojis + submit land together
  const ratingDone   = p >= 0.15;                          // small delay so the empty state is visible first
  const buttonActive = p >= 0.30;                          // breathing room after the rating
  const buttonBlink  = p >= 0.30;                          // button blinks orange to invite the tap

  return (
    <div style={{ height: "100%", background: "#fff", display: "flex", flexDirection: "column", padding: "42px 14px 14px", position: "relative", color: "#0E0E0E", opacity: screenOp, transform: `translateY(${(1 - screenOp) * 8}px)` }}>
      {/* Pizzeria logo */}
      <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
        <PizzaIcon size={24} />
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.015em", color: "#0E0E0E" }}>{t("how.phone.restaurant")}</span>
      </div>

      <div style={{ flex: 1, minHeight: 8 }} />

      {/* Question card */}
      <div style={{ background: "#F4F4F4", borderRadius: 14, padding: "14px 12px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, textAlign: "center", lineHeight: 1.3 }}>{t("how.phone.question")}</div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-around", alignItems: "flex-end" }}>
          <EmojiOption type="meh"  label={t("how.phone.ratings.meh")}   dim={ratingDone} />
          <EmojiOption type="nice" label={t("how.phone.ratings.nice")}  dim={ratingDone} />
          <EmojiOption type="love" label={t("how.phone.ratings.love")}  selected={ratingDone} />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 8 }} />

      {/* Submit */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!buttonActive}
        style={{
          padding: "13px 16px",
          borderRadius: 99,
          background: buttonActive ? "#0E0E0E" : "#BDBDBD",
          color: "#FFF",
          fontSize: 13,
          fontWeight: 700,
          border: 0,
          cursor: buttonActive ? "pointer" : "default",
          animation: buttonBlink ? "tt-sendblink 1.1s ease-in-out infinite" : "none",
          pointerEvents: buttonActive ? "auto" : "none",
          transition: "background .2s"
        }}
      >
        {t("how.phone.submit")}
      </button>
    </div>
  );
}

function EmojiOption({ type, label, selected = false, dim = false }) {
  const opacity = selected ? 1 : (dim ? 0.22 : 1);
  return (
    <div
      style={{
        position: "relative",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        padding: "8px 10px", borderRadius: 12,
        background: selected ? "rgba(255, 90, 26, 0.10)" : "transparent",
        boxShadow: selected ? "0 0 0 2px #FF5A1A, 0 8px 20px -10px rgba(255,90,26,.55)" : "none",
        transform: selected ? "scale(1.06)" : "scale(1)",
        opacity,
        filter: dim && !selected ? "grayscale(.65)" : "none",
        transition: "opacity .35s ease, background .35s ease, transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s ease, filter .35s ease"
      }}
    >
      <EmojiIcon type={type} size={36} />
      <span style={{ fontSize: 10, fontWeight: selected ? 700 : 600, color: selected ? "#0E0E0E" : "inherit" }}>{label}</span>
      {selected && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute", top: -5, right: -5,
            width: 16, height: 16, borderRadius: 99,
            background: "#FF5A1A",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 6px rgba(255,90,26,.45)"
          }}
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6.5l2.2 2.2L9.5 3.7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </div>
  );
}

function PizzaIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.5 L 21.5 19 C 17 21.5, 7 21.5, 2.5 19 Z" fill="#F0B845" stroke="#7A4B12" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M2.5 19 C 7 21.5, 17 21.5, 21.5 19" stroke="#7A4B12" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <circle cx="12" cy="10" r="1.5" fill="#D14025" />
      <circle cx="9" cy="15" r="1.4" fill="#D14025" />
      <circle cx="15" cy="15" r="1.4" fill="#D14025" />
      <circle cx="13.5" cy="13" r=".7" fill="#8FBF6B" />
      <circle cx="10.5" cy="11.5" r=".7" fill="#8FBF6B" />
    </svg>
  );
}

function EmojiIcon({ type, size = 36 }) {
  if (type === "meh") {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40">
        <path d="M20 4 C 10 4, 6 12, 6 20 V 36 H 34 V 20 C 34 12, 30 4, 20 4 Z" fill="#74B1F0" />
        <circle cx="14" cy="20" r="2" fill="#0E0E0E" />
        <circle cx="26" cy="20" r="2" fill="#0E0E0E" />
        <path d="M14 28 Q 20 24 26 28" stroke="#0E0E0E" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "nice") {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40">
        <path d="M20 4 C 10 4, 6 12, 6 20 V 28 C 6 34, 12 36, 20 36 C 28 36, 34 34, 34 28 V 20 C 34 12, 30 4, 20 4 Z" fill="#F5B547" />
        <circle cx="14" cy="18" r="2" fill="#0E0E0E" />
        <circle cx="26" cy="18" r="2" fill="#0E0E0E" />
        <path d="M14 24 Q 20 30 26 24" stroke="#0E0E0E" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "love") {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40">
        <path d="M20 36 C 4 25, 2 16, 2 11 C 2 6, 6 3, 11 3 C 15 3, 18 5, 20 8 C 22 5, 25 3, 29 3 C 34 3, 38 6, 38 11 C 38 16, 36 25, 20 36 Z" fill="#FF5A1A" />
        <circle cx="14" cy="14" r="2" fill="#0E0E0E" />
        <circle cx="26" cy="14" r="2" fill="#0E0E0E" />
        <path d="M14 20 Q 20 25 26 20" stroke="#0E0E0E" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  return null;
}

/* --------------------------- manager screen (P2) ------------------------- */

function ManagerScreen({ p, onFix }) {
  const { t } = useTranslation();
  const headerOp  = clamp01((p - 0.00) / 0.08);
  const dateOp    = clamp01((p - 0.08) / 0.08);
  const statsOp   = clamp01((p - 0.16) / 0.10);
  const notif1Op  = clamp01((p - 0.30) / 0.06);
  const notif2Op  = clamp01((p - 0.40) / 0.06);
  const notif3Op  = clamp01((p - 0.50) / 0.06);
  const highlight = p >= 0.66;
  const fixOp     = clamp01((p - 0.70) / 0.06);

  const notifData = t("how.manager.notifs", { returnObjects: true });
  const reveals = [notif1Op, notif2Op, notif3Op];
  const negatives = [true, false, false];
  const emojis = ["meh", "nice", "nice"];
  const notifs = notifData.map((n, i) => ({ ...n, reveal: reveals[i], negative: negatives[i], emoji: emojis[i] }));

  return (
    <div style={{ height: "100%", background: "#fff", display: "flex", flexDirection: "column", color: "#0E0E0E", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ marginTop: 36, padding: "12px 12px 8px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", opacity: headerOp, background: "#fff" }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, display: "flex", alignItems: "center", gap: 4, letterSpacing: "-.01em" }}>
            {t("how.manager.restaurantName")}
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M3 4.5l3 3 3-3" stroke="#0E0E0E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{ marginTop: 1, fontSize: 8.5, color: "#888", display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: 9 }}>◉</span>{t("how.manager.location")}
          </div>
        </div>
        <div style={{ width: 26, height: 26, borderRadius: 99, background: "#F4F4F4", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 4a5 5 0 00-5 5v3l-2 4h14l-2-4V9a5 5 0 00-5-5z" stroke="#0E0E0E" strokeWidth="1.6" strokeLinejoin="round"/><path d="M10 19a2 2 0 004 0" stroke="#0E0E0E" strokeWidth="1.6"/></svg>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, background: "#EFEFEF", padding: "10px 10px 10px", display: "flex", flexDirection: "column", gap: 8, overflow: "hidden", minHeight: 0 }}>

        {/* Date range */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", opacity: dateOp }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: "#FFE3CC", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="2" stroke="#FF5A1A" strokeWidth="1.4"/><path d="M2 6h12M6 2v3M10 2v3" stroke="#FF5A1A" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.1 }}>{t("how.manager.dateLabel")}</div>
              <div style={{ fontSize: 8.5, color: "#777" }}>{t("how.manager.dateRange")}</div>
            </div>
          </div>
          <span style={{ fontSize: 11, color: "#888" }}>▾</span>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, opacity: statsOp }}>
          {[
            { type: "love", count: 80, pct: "54%" },
            { type: "nice", count: 37, pct: "25%" },
            { type: "meh",  count: 30, pct: "20%" }
          ].map((s, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "10px 4px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <EmojiIcon type={s.type} size={22} />
              <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.05 }}>{s.count}</div>
              <div style={{ fontSize: 9, color: "#777" }}>{s.pct}</div>
            </div>
          ))}
        </div>

        {/* Recent feedback */}
        <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: ".12em", color: "#888", marginTop: 2, opacity: notif1Op }}>{t("how.manager.recentFeedback")}</div>

        <div style={{ background: "#fff", borderRadius: 10, padding: 4, display: "flex", flexDirection: "column", gap: 2 }}>
          {notifs.map((n, i) => (
            <NotifRow key={i} notif={n} highlight={highlight} />
          ))}
        </div>

        {/* Fix now button */}
        <button
          type="button"
          onClick={onFix}
          disabled={fixOp < 1}
          style={{
            marginTop: 4,
            padding: "10px 14px",
            borderRadius: 99,
            background: "#FF5A1A",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            border: 0,
            cursor: fixOp >= 1 ? "pointer" : "default",
            opacity: fixOp,
            transform: `translateY(${(1 - fixOp) * 8}px)`,
            animation: fixOp >= 1 ? "tt-fixblink 1.2s ease-in-out infinite" : "none",
            pointerEvents: fixOp >= 1 ? "auto" : "none"
          }}
        >
          {t("how.manager.fixNow")}
        </button>
      </div>

      {/* Bottom nav */}
      <div style={{ display: "flex", borderTop: "1px solid #E5E5E5", padding: "5px 4px 6px", background: "#fff", opacity: headerOp }}>
        {[
          { l: t("how.manager.nav.dashboard"),   active: true,  icon: "grid" },
          { l: t("how.manager.nav.feedback"),    active: false, icon: "chat" },
          { l: t("how.manager.nav.performance"), active: false, icon: "bars" },
          { l: t("how.manager.nav.recover"),     active: false, icon: "life" },
          { l: t("how.manager.nav.settings"),    active: false, icon: "cog"  }
        ].map((tab, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, color: tab.active ? "#FF5A1A" : "#9A9A9A" }}>
            <NavIcon name={tab.icon} size={14} />
            <span style={{ fontSize: 7.5, fontWeight: tab.active ? 700 : 500 }}>{tab.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotifRow({ notif, highlight }) {
  const desaturate = highlight && !notif.negative;
  const blink = highlight && notif.negative;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "7px 6px",
        opacity: notif.reveal,
        transform: `translateY(${(1 - notif.reveal) * 6}px)`,
        filter: desaturate ? "grayscale(1) opacity(.40)" : "none",
        background: "#fff",
        borderRadius: 8,
        transition: "filter .4s",
        animation: blink ? "tt-notifblink 1.2s ease-in-out infinite" : "none",
        position: "relative"
      }}
    >
      <EmojiIcon type={notif.emoji} size={18} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{notif.name}</div>
        <div style={{ fontSize: 9, color: "#777", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{notif.sub}</div>
      </div>
      <div style={{ fontSize: 8, color: "#999", textAlign: "right", lineHeight: 1.3, flexShrink: 0 }}>{notif.time}</div>
    </div>
  );
}

function NavIcon({ name, size = 14 }) {
  const s = size;
  if (name === "grid") return (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1" fill="currentColor"/><rect x="9" y="2" width="5" height="5" rx="1" fill="currentColor"/><rect x="2" y="9" width="5" height="5" rx="1" fill="currentColor"/><rect x="9" y="9" width="5" height="5" rx="1" fill="currentColor"/></svg>);
  if (name === "chat") return (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 4a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6l-3 3v-3a2 2 0 010-4V4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>);
  if (name === "bars") return (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 14V8m5 6V4m5 10V10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>);
  if (name === "life") return (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/><path d="M3.5 3.5l2.5 2.5M10 10l2.5 2.5M3.5 12.5L6 10M10 6l2.5-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>);
  if (name === "cog")  return (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.4"/><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>);
  return null;
}

/* ------------------------------- fallback ------------------------------- */

function StaticFallback() {
  const { t } = useTranslation();
  return (
    <section style={{ padding: "120px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", background: "var(--bg)" }}>
      <div className="container">
        <Eyebrow>{t("how.eyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-xl" style={{ marginTop: 16, maxWidth: 900 }}>
          {t("how.headline")}<br /><span style={{ color: "var(--persimmon)" }}>{t("how.headlineItalic")}</span>
        </h2>
        <div style={{ marginTop: 64, display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
          <NFCCard size="md" />
          <IPhoneFrame><FeedbackScreen p={0.85} onSubmit={() => {}} /></IPhoneFrame>
          <IPhoneFrame><ManagerScreen p={0.85} onFix={() => {}} /></IPhoneFrame>
        </div>
      </div>
    </section>
  );
}
